import { DraftAllStatus, Proposal, ProposalStatus, ProposalSummary, ProposalTemplate } from '../types/proposal';
import { apiClient } from './api';

/**
 * 제안서 — `projects/:id/proposal/*`. 2026-09-29 부터 실제 DB. 그전 목(`mocks/proposalApi.ts`)은 지웠다.
 * AI 초안은 서버가 자료를 모아 쓴다 — 화면은 칸 코드만 보낸다.
 */
export const proposalApi = {
  templates(): Promise<ProposalTemplate[]> {
    return apiClient.get('/proposal/templates');
  },

  /** 최신 판 + 공고로 제안한 양식 + 양식 목록 */
  summary(projectId: number): Promise<ProposalSummary> {
    return apiClient.get(`/projects/${projectId}/proposal`);
  },

  /** 양식 칸을 깐다. 작성 중인 판이 있으면 그것을 돌려준다 */
  create(projectId: number, templateCode?: string): Promise<Proposal> {
    return apiClient.post(`/projects/${projectId}/proposal`, { templateCode });
  },

  setStatus(projectId: number, status: ProposalStatus): Promise<Proposal> {
    return apiClient.patch(`/projects/${projectId}/proposal/status`, { status });
  },

  updateSection(projectId: number, code: string, patch: { content?: string; title?: string }): Promise<Proposal> {
    return apiClient.patch(`/projects/${projectId}/proposal/sections/${encodeURIComponent(code)}`, patch);
  },

  /** 칸을 더한다. `basisCode`(공통 칸 1~6)를 주면 그 칸의 채움 방식 · 지침 · 룰을 받는다. 없으면 `fill` 이 필요하다 */
  addSection(projectId: number, body: { code: string; title: string; parentCode?: string; fill?: 'AI' | '직접'; basisCode?: string }): Promise<Proposal> {
    return apiClient.post(`/projects/${projectId}/proposal/sections`, body);
  },

  /** 칸 ↔ 배점 항목 — 통째로 바꾼다. 조견표의 「해당 칸」이 된다 */
  setSectionEvals(projectId: number, code: string, evalItemIds: number[]): Promise<Proposal> {
    return apiClient.put(`/projects/${projectId}/proposal/sections/${encodeURIComponent(code)}/evals`, { evalItemIds });
  },

  removeSection(projectId: number, code: string): Promise<Proposal> {
    return apiClient.delete(`/projects/${projectId}/proposal/sections/${encodeURIComponent(code)}`);
  },

  /** 룰 칸 · 자료로 미리 채우는 직접 칸. 근거가 없으면 `reason` 과 함께 그대로 */
  fillRule(projectId: number, code: string): Promise<{ proposal: Proposal; reason?: string }> {
    return apiClient.post(`/projects/${projectId}/proposal/sections/${encodeURIComponent(code)}/fill`);
  },

  draft(projectId: number, code: string, instruction?: string): Promise<Proposal> {
    return apiClient.post(`/projects/${projectId}/proposal/sections/${encodeURIComponent(code)}/draft`, { instruction });
  },

  approve(projectId: number, code: string): Promise<Proposal> {
    return apiClient.post(`/projects/${projectId}/proposal/sections/${encodeURIComponent(code)}/approve`);
  },

  discard(projectId: number, code: string): Promise<Proposal> {
    return apiClient.post(`/projects/${projectId}/proposal/sections/${encodeURIComponent(code)}/discard`);
  },

  /** 룰 칸 먼저, AI 칸은 의존 순서로 전부. 바로 돌아오고 진행은 status 로 본다 */
  draftAll(projectId: number, redo = false): Promise<DraftAllStatus> {
    return apiClient.post(`/projects/${projectId}/proposal/draft-all`, { redo });
  },

  draftAllStatus(projectId: number): Promise<DraftAllStatus> {
    return apiClient.get(`/projects/${projectId}/proposal/draft-all/status`);
  },

  /** 칸 전체를 순서대로 한 텍스트로 */
  /** PPTX — 서버가 만든 파일을 받는다 */
  exportPptx(projectId: number): Promise<Blob> {
    return apiClient.getBlob(`/projects/${projectId}/proposal/export.pptx`);
  },

  exportText(projectId: number): Promise<{ fileName: string; text: string }> {
    return apiClient.get(`/projects/${projectId}/proposal/export`);
  },
};
